// src/pages/Variations.tsx
// CM-02: Variations Module — notice deadline tracking, register, running contract sums
// A13 CA Agent sidebar integrated — live Anthropic API
// Gap status: ABSENT → BUILT

import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_VARIATIONS, MOCK_CONTRACTS,
  getVariationNoticeDaysLeft, getNoticeUrgency,
} from '../utils/mockDataCommercial'
import { AgentSidebar } from '../components/AgentSidebar'
import {
  AlertTriangle, CheckCircle, Clock, ChevronRight, X,
  FileText, Plus, Printer, Search, DollarSign, Calendar, Building2,
} from 'lucide-react'

// ── Formatters ────────────────────────────────────────────────────
const fmtAUD  = (n?: number | null) => n != null
  ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(n)
  : '—'
const fmtDate = (iso?: string | null) => iso
  ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—'

// ── Metadata ──────────────────────────────────────────────────────
const STATUS_META: Record<string, { label: string; bg: string; text: string }> = {
  DRAFT:     { label: 'Draft',     bg: 'bg-gray-100',  text: 'text-gray-600'  },
  SUBMITTED: { label: 'Submitted', bg: 'bg-blue-100',  text: 'text-blue-700'  },
  APPROVED:  { label: 'Approved',  bg: 'bg-green-100', text: 'text-green-700' },
  REJECTED:  { label: 'Rejected',  bg: 'bg-red-100',   text: 'text-red-700'   },
}
const TYPE_LABEL: Record<string, string> = {
  DIRECTED_VARIATION: 'Directed variation',
  CLIENT_INSTRUCTION: 'Client instruction',
  CHANGED_CONDITIONS: 'Changed conditions',
}
const CONTRACT_LABEL: Record<string, string> = {
  AS2124: 'AS2124', AS4000: 'AS4000', NEC4: 'NEC4', PANEL_SOA: 'SOA Panel',
}
const TIME_BAR: Record<string, string> = {
  AS2124: 'AS2124 Cl 40.1 — 28-day time bar',
  AS4000: 'AS4000 Cl 41.1 — 14-day time bar',
  NEC4:   'NEC4 Cl 61.3 — 8-week notice required',
}

// ── Urgency badge ─────────────────────────────────────────────────
function UrgencyBadge({ vr }: { vr: typeof MOCK_VARIATIONS[0] }) {
  if (vr.status === 'APPROVED' || vr.status === 'REJECTED') {
    return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Resolved</span>
  }
  if (vr.noticeIssuedDate) {
    return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Notice issued {fmtDate(vr.noticeIssuedDate)}</span>
  }
  const days    = getVariationNoticeDaysLeft(vr)
  const urgency = getNoticeUrgency(days)
  if (urgency === 'OK')     return <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">No deadline</span>
  const isMissed = urgency === 'MISSED'
  const label = isMissed
    ? `MISSED — ${Math.abs(days!)}d ago`
    : urgency === 'URGENT'
      ? `URGENT — ${days}d left`
      : `WARN — ${days}d left`
  const cls = isMissed || urgency === 'URGENT'
    ? 'bg-red-100 text-red-700'
    : 'bg-amber-100 text-amber-700'
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isMissed ? 'bg-red-500' : urgency === 'URGENT' ? 'bg-red-500' : 'bg-amber-500'}`}/>
      {label}
    </span>
  )
}

// ── Variation detail panel ────────────────────────────────────────
function VariationDetail({ vr, onClose }: { vr: typeof MOCK_VARIATIONS[0]; onClose: () => void }) {
  const contract = MOCK_CONTRACTS.find(c => c.id === vr.contractId)
  const days     = getVariationNoticeDaysLeft(vr)
  const urgency  = getNoticeUrgency(days)
  const sm       = STATUS_META[vr.status] ?? STATUS_META.DRAFT
  const showAlert = !vr.noticeIssuedDate && vr.status !== 'APPROVED' && (urgency === 'URGENT' || urgency === 'MISSED' || urgency === 'WARN')

  return (
    <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 160px)' }}>
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b bg-gray-50">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-gray-400 font-semibold">{vr.variationNumber}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
          </div>
          <h2 className="text-sm font-semibold text-gray-900 leading-snug max-w-xs">{vr.description}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{TYPE_LABEL[vr.type] ?? vr.type} · {vr.jobNumber}</p>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg ml-3 flex-shrink-0">
          <X className="w-4 h-4 text-gray-400"/>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {/* Alert */}
        {showAlert && (
          <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${urgency === 'WARN' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
            <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${urgency === 'WARN' ? 'text-amber-600' : 'text-red-600'}`}/>
            <div>
              <p className={`text-xs font-semibold ${urgency === 'WARN' ? 'text-amber-800' : 'text-red-800'}`}>
                {urgency === 'MISSED' ? 'Notice deadline has passed' : urgency === 'URGENT' ? `Notice required within ${days} day${days !== 1 ? 's' : ''}` : `Notice due in ${days} days`}
              </p>
              {contract && (
                <p className="text-xs text-gray-600 mt-0.5">{TIME_BAR[contract.contractType] ?? 'Check contract conditions for time bar.'}</p>
              )}
            </div>
          </div>
        )}

        {/* Key dates */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Notice due',     value: fmtDate(vr.noticeDueDate),    warn: (urgency === 'URGENT' || urgency === 'MISSED') && !vr.noticeIssuedDate },
            { label: 'Notice issued',  value: vr.noticeIssuedDate ? fmtDate(vr.noticeIssuedDate) : 'Not yet issued', warn: false },
            { label: 'Submitted',      value: vr.submittedDate ? fmtDate(vr.submittedDate) : '—', warn: false },
            { label: 'Contract',       value: contract?.contractNumber ?? '—', warn: false },
          ].map(({ label, value, warn }) => (
            <div key={label} className={`rounded-xl border px-3 py-2.5 ${warn ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'}`}>
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">{label}</div>
              <div className={`text-sm font-semibold ${warn ? 'text-red-700' : 'text-gray-900'}`}>{value}</div>
            </div>
          ))}
        </div>

        {/* Cost lines */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Cost breakdown</h3>
          <div className="space-y-1">
            {vr.costLines.map((line, i) => (
              <div key={i} className="flex justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                <span className="text-gray-600 pr-3">{line.description}</span>
                <span className="font-mono font-medium text-gray-900 flex-shrink-0">{fmtAUD(line.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between text-xs pt-2 font-semibold border-t border-gray-200 mt-1">
              <span className="text-gray-700">Total ex GST</span>
              <span className="font-mono">{fmtAUD(vr.totalExGST)}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span>GST (10%)</span>
              <span className="font-mono">{fmtAUD(vr.gstAmount)}</span>
            </div>
            <div className="flex justify-between text-sm font-bold border-t border-gray-200 pt-1.5">
              <span>Total inc GST</span>
              <span className="font-mono text-[#1F3864]">{fmtAUD(vr.totalIncGST)}</span>
            </div>
            {vr.approvedAmount != null && (
              <div className="flex justify-between text-xs pt-1 text-green-700 font-semibold">
                <span>Approved amount</span>
                <span className="font-mono">{fmtAUD(vr.approvedAmount)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Contract info */}
        {contract && (
          <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-xs space-y-1">
            {[
              ['Type', CONTRACT_LABEL[contract.contractType] ?? contract.contractType],
              ['Client', contract.clientName],
              ['Superintendent', contract.superintendentName],
              ['Original value', fmtAUD(contract.originalContractValue)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-gray-500">{k}</span>
                <span className="font-medium text-gray-900">{v}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border-t bg-white px-5 py-4 flex gap-2 flex-wrap">
        {!vr.noticeIssuedDate && vr.status !== 'APPROVED' && (
          <button className="btn-primary text-sm flex items-center gap-2">
            <FileText className="w-3.5 h-3.5"/>Issue notice letter
          </button>
        )}
        {vr.status === 'DRAFT' && vr.noticeIssuedDate && (
          <button className="btn-primary text-sm">Submit variation</button>
        )}
        {vr.status === 'SUBMITTED' && (
          <>
            <button className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-2 rounded-lg font-medium">Record approval</button>
            <button className="btn-secondary text-sm text-red-600">Record rejection</button>
          </>
        )}
        <button className="btn-secondary text-sm flex items-center gap-2 ml-auto">
          <Printer className="w-3.5 h-3.5"/>Print
        </button>
      </div>
    </div>
  )
}

// ── Printable register ────────────────────────────────────────────
function printVariationRegister() {
  const rows = MOCK_VARIATIONS.map(vr => {
    const days    = getVariationNoticeDaysLeft(vr)
    const urgency = getNoticeUrgency(days)
    const ns = vr.noticeIssuedDate
      ? `Issued ${fmtDate(vr.noticeIssuedDate)}`
      : urgency === 'MISSED' ? 'MISSED'
      : urgency === 'URGENT' ? `URGENT (${days}d)`
      : urgency === 'WARN'   ? `WARN (${days}d)`
      : '—'
    const sm = STATUS_META[vr.status]
    const rowBg = urgency === 'MISSED' || (urgency === 'URGENT' && !vr.noticeIssuedDate) ? '#fef2f2' : ''
    return `<tr style="background:${rowBg}">
      <td>${vr.variationNumber}</td>
      <td>${vr.jobNumber}</td>
      <td>${vr.description}</td>
      <td>${TYPE_LABEL[vr.type] ?? vr.type}</td>
      <td style="color:${urgency==='MISSED'||urgency==='URGENT'?'#dc2626':urgency==='WARN'?'#d97706':'inherit'}">${ns}</td>
      <td>${sm?.label ?? vr.status}</td>
      <td style="text-align:right">${fmtAUD(vr.totalExGST)}</td>
      <td style="text-align:right">${vr.approvedAmount != null ? fmtAUD(vr.approvedAmount) : '—'}</td>
    </tr>`
  }).join('')

  const html = `<!DOCTYPE html><html><head><title>Variation Register</title>
  <style>body{font-family:Calibri,sans-serif;font-size:11px;margin:20px}h1{font-size:16px;color:#1F3864}
  table{width:100%;border-collapse:collapse;margin-top:12px}
  th{background:#1F3864;color:white;padding:6px 8px;text-align:left;font-size:10px}
  td{padding:5px 8px;border-bottom:1px solid #e5e7eb}
  tr:nth-child(even){background:#f9fafb}
  .footer{margin-top:24px;font-size:9px;color:#6b7280}</style>
  </head><body>
  <h1>Variation Register — MCB Civil Electrical Pty Ltd</h1>
  <div style="font-size:10px;color:#6b7280;margin-bottom:8px">Generated ${new Date().toLocaleString('en-AU')} · TrafficProcure MCBCE-K</div>
  <table><thead><tr><th>VR#</th><th>Job</th><th>Description</th><th>Type</th><th>Notice status</th><th>Status</th><th>Ex GST</th><th>Approved</th></tr></thead>
  <tbody>${rows}</tbody></table>
  <div class="footer">MCB Civil Electrical Pty Ltd ABN 44 128 993 087 · Confidential</div>
  </body></html>`
  const w = window.open('', '_blank', 'width=900,height=700')
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 300) }
}

// ── Main page ─────────────────────────────────────────────────────
export function Variations() {
  const { isTier } = useAuthStore()
  const canEdit = isTier('B_PM') || isTier('B_PC') || isTier('A_DEVELOPER')

  const [search, setSearch]     = useState('')
  const [jobFilter, setJobFilter] = useState('all')
  const [selected, setSelected] = useState<typeof MOCK_VARIATIONS[0] | null>(null)
  const [showNew, setShowNew]   = useState(false)

  // Stats
  const alertCount   = MOCK_VARIATIONS.filter(v => {
    if (v.status === 'APPROVED' || v.noticeIssuedDate) return false
    const u = getNoticeUrgency(getVariationNoticeDaysLeft(v))
    return u === 'URGENT' || u === 'WARN' || u === 'MISSED'
  }).length
  const totalApproved  = MOCK_VARIATIONS.filter(v => v.status === 'APPROVED').reduce((s, v) => s + (v.approvedAmount ?? 0), 0)
  const totalSubmitted = MOCK_VARIATIONS.filter(v => v.status === 'SUBMITTED').reduce((s, v) => s + v.totalExGST, 0)

  // Running contract sums
  const contractSums = MOCK_CONTRACTS.map(con => {
    const approved = MOCK_VARIATIONS.filter(v => v.contractId === con.id && v.status === 'APPROVED').reduce((s, v) => s + (v.approvedAmount ?? 0), 0)
    return { ...con, approvedVarTotal: approved, runningSum: con.originalContractValue + approved }
  })

  // Jobs for filter
  const jobs = useMemo(() => {
    const seen = new Set<string>()
    return MOCK_VARIATIONS.filter(v => !seen.has(v.jobId) && seen.add(v.jobId)).map(v => ({ id: v.jobId, number: v.jobNumber }))
  }, [])

  const filtered = MOCK_VARIATIONS.filter(v => {
    if (jobFilter !== 'all' && v.jobId !== jobFilter) return false
    const q = search.toLowerCase()
    if (q && !v.variationNumber.toLowerCase().includes(q) && !v.description.toLowerCase().includes(q)) return false
    return true
  })

  // URGENT/MISSED alert banners
  const criticalVars = MOCK_VARIATIONS.filter(v => {
    if (v.noticeIssuedDate || v.status === 'APPROVED') return false
    const u = getNoticeUrgency(getVariationNoticeDaysLeft(v))
    return u === 'URGENT' || u === 'MISSED'
  })

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Variations</h1>
          <p className="text-sm text-gray-500 mt-0.5">Notice deadline tracking · variation register · running contract sum management</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={printVariationRegister} className="btn-secondary text-sm flex items-center gap-2">
            <Printer className="w-4 h-4"/>Print register
          </button>
          {canEdit && (
            <button onClick={() => setShowNew(true)} className="btn-primary text-sm flex items-center gap-2">
              <Plus className="w-4 h-4"/>New variation
            </button>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Variations on foot',   value: MOCK_VARIATIONS.length,                                    color: 'text-gray-900',   sub: `${MOCK_VARIATIONS.filter(v=>v.status==='DRAFT').length} draft · ${MOCK_VARIATIONS.filter(v=>v.status==='SUBMITTED').length} submitted` },
          { label: 'Notice alerts',         value: alertCount,                                                 color: alertCount > 0 ? 'text-red-600' : 'text-green-700', sub: alertCount > 0 ? 'Require notice action' : 'All notices current' },
          { label: 'Approved (ex GST)',     value: fmtAUD(totalApproved),                                      color: 'text-green-700',  sub: `${MOCK_VARIATIONS.filter(v=>v.status==='APPROVED').length} approved` },
          { label: 'Submitted (pending)',   value: fmtAUD(totalSubmitted),                                     color: 'text-blue-700',   sub: 'Awaiting client response' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {/* A13 CA Agent sidebar */}
      <AgentSidebar context={{
        page: 'variations',
        variations: MOCK_VARIATIONS,
        contracts:  MOCK_CONTRACTS,
        platformDate: '2026-06-17',
      }} />

      {/* Running contract sums */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Running contract sums</span>
          <span className="text-xs text-gray-400">Original + approved variations</span>
        </div>
        <div className="divide-y divide-gray-50">
          {contractSums.map(con => (
            <div key={con.id} className="px-4 py-3 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-900">{con.jobNumber}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                    con.contractType === 'AS2124' ? 'bg-blue-50 text-blue-700' :
                    con.contractType === 'AS4000' ? 'bg-indigo-50 text-indigo-700' :
                    con.contractType === 'NEC4'   ? 'bg-purple-50 text-purple-700' :
                    'bg-teal-50 text-teal-700'
                  }`}>{CONTRACT_LABEL[con.contractType] ?? con.contractType}</span>
                  <span className="text-xs text-gray-400">{con.clientName}</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0 text-xs">
                <span className="text-gray-400">Original: </span>
                <span className="font-mono text-gray-700">{fmtAUD(con.originalContractValue)}</span>
                {con.approvedVarTotal > 0 && (
                  <span className="ml-2 text-green-600 font-mono">+ {fmtAUD(con.approvedVarTotal)}</span>
                )}
              </div>
              <div className="text-right flex-shrink-0 w-32">
                <div className="text-[10px] text-gray-400 mb-0.5">Running sum</div>
                <div className="text-sm font-bold text-[#1F3864] font-mono">{fmtAUD(con.runningSum)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Critical alert banners */}
      {criticalVars.map(vr => {
        const days    = getVariationNoticeDaysLeft(vr)
        const urgency = getNoticeUrgency(days)
        return (
          <div key={vr.id} className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"/>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">
                {urgency === 'MISSED' ? `Notice deadline MISSED: ${vr.variationNumber}` : `Notice required immediately: ${vr.variationNumber} — ${days}d remaining`}
              </p>
              <p className="text-xs text-red-700 mt-0.5">{vr.description} · {vr.jobNumber} · Due: {fmtDate(vr.noticeDueDate)}</p>
            </div>
            <button onClick={() => setSelected(vr)} className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2.5 py-1.5 rounded-lg font-medium flex-shrink-0">
              View &amp; action
            </button>
          </div>
        )
      })}

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search variations…" className="input pl-9 text-sm"/>
        </div>
        <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input text-sm w-48">
          <option value="all">All jobs</option>
          {jobs.map(j => <option key={j.id} value={j.id}>{j.number}</option>)}
        </select>
      </div>

      {/* Register + detail panel */}
      <div className="flex gap-4 items-start">
        <div className="flex-1 min-w-0 space-y-2">
          {filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-200 rounded-xl py-16 text-center text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300"/>
              <p className="font-medium text-gray-500">No variations found</p>
            </div>
          ) : filtered.map(vr => {
            const days       = getVariationNoticeDaysLeft(vr)
            const urgency    = getNoticeUrgency(days)
            const isSelected = selected?.id === vr.id
            const sm         = STATUS_META[vr.status] ?? STATUS_META.DRAFT
            const contract   = MOCK_CONTRACTS.find(c => c.id === vr.contractId)
            const isCritical = !vr.noticeIssuedDate && vr.status !== 'APPROVED' && (urgency === 'URGENT' || urgency === 'MISSED')
            return (
              <div key={vr.id}
                onClick={() => setSelected(isSelected ? null : vr)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${
                  isSelected    ? 'border-blue-400 ring-2 ring-blue-100 shadow-sm' :
                  isCritical    ? 'border-red-300 bg-red-50/30' :
                  urgency === 'WARN' && !vr.noticeIssuedDate ? 'border-amber-200 bg-amber-50/20' :
                  'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs font-semibold text-gray-500">{vr.variationNumber}</span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{TYPE_LABEL[vr.type] ?? vr.type}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mb-1.5 leading-snug">{vr.description}</p>
                    <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3"/>{vr.jobNumber}</span>
                      {contract && <span>{CONTRACT_LABEL[contract.contractType] ?? contract.contractType} · {contract.clientName}</span>}
                      <span className="font-mono">{fmtAUD(vr.totalExGST)}</span>
                      {vr.approvedAmount != null && <span className="text-green-600 font-medium">✓ {fmtAUD(vr.approvedAmount)} approved</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <UrgencyBadge vr={vr}/>
                    <ChevronRight className={`w-4 h-4 ${isSelected ? 'text-blue-500' : 'text-gray-300'}`}/>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="w-[460px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm sticky top-6">
            <VariationDetail vr={selected} onClose={() => setSelected(null)}/>
          </div>
        )}
      </div>

      {/* New variation modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">New variation</h2>
              <button onClick={() => setShowNew(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4"/></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Contract</label>
                <select className="input text-sm w-full">
                  {MOCK_CONTRACTS.map(c => <option key={c.id} value={c.id}>{c.jobNumber} — {c.clientName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Variation type</label>
                <select className="input text-sm w-full">
                  <option value="DIRECTED_VARIATION">Directed variation</option>
                  <option value="CLIENT_INSTRUCTION">Client instruction</option>
                  <option value="CHANGED_CONDITIONS">Changed conditions</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input type="text" className="input text-sm w-full" placeholder="Describe the variation event"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Notice due date</label>
                  <input type="date" className="input text-sm w-full"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Estimated value (ex GST)</label>
                  <input type="number" className="input text-sm w-full" placeholder="0.00"/>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={() => setShowNew(false)} className="btn-primary text-sm">Create variation</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
